import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { WhiteLabelProvider } from './contexts/WhiteLabelContext';
import App from './App.tsx';
import './index.css';

const queryClient = new QueryClient();

// Boot tracking
console.log('[Boot] main.tsx loaded');
console.log('[Boot] Version:', typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.0.0');

// Initialize Sentry only in production with proper error handling
try {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    // Add boot start breadcrumb
    Sentry.addBreadcrumb({
      category: 'boot',
      message: 'BOOT_START',
      level: 'info',
      timestamp: Date.now() / 1000,
    });

    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      // Phase 3: Full trace sampling for baseline establishment
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      
      // Track custom performance metrics
      beforeSendTransaction(event) {
        // Add TTFV baseline tracking
        if (event.contexts?.trace) {
          event.tags = {
            ...event.tags,
            performance_baseline: 'true',
          };
        }
        return event;
      },
      
      beforeSend(event) {
        // Filter out non-critical errors
        if (event.level === 'warning' || event.level === 'info') {
          return null;
        }
        return event;
      },
    });

    // Expose Sentry for custom metrics tracking
    (window as any).Sentry = Sentry;
  }
} catch (error) {
  console.warn('Sentry initialization failed:', error);
}

console.log('[Boot] Creating React root');
const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error('Root element #root not found');
}
const root = createRoot(rootEl);

// Mark mounted only after first paint to avoid masking real boot errors
const markMounted = () => {
  document.documentElement.dataset.appMounted = 'true';
  (window as any).__APP_MOUNTED = true;
  try { window.dispatchEvent(new Event('app:mounted')); } catch {}
};

try {
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <WhiteLabelProvider>
          <App />
        </WhiteLabelProvider>
      </QueryClientProvider>
    </StrictMode>
  );
  // After initial paint
  requestAnimationFrame(() => markMounted());
} catch (e: any) {
  console.error('[Boot] root.render failed', e);
  (window as any).__BOOT_ERRORS?.push?.({ type: 'render_error', message: String(e?.message || e) });
  // Ensure overlay doesn't loop forever and show a minimal fallback
  document.documentElement.dataset.appMounted = 'true';
  (window as any).__APP_MOUNTED = false; // mounted flag false but attribute true to bypass overlay
  rootEl.innerHTML = '<div style="padding:16px;color:#fff;background:#111;font-family:system-ui,Segoe UI,Roboto,Helvetica,Arial,sans-serif">App failed to start. Please use Clear App Cache, then Reload.</div>';
}

// Removed forced APP_MOUNTED timeout to avoid masking boot errors
